import React, { useCallback, useState } from 'react';
import { useAuth } from '../../context/Auth';
import { ActivityIndicator, Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../services/window-information';
import CustomButton from '../../components/CustomButton';
import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import Divider from '../../components/Divider';
import SmartSearchFacialComparer from './SmartSearchFacialComparer';
import Map from '../../components/Map';
import { ScrollView } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCalendar, faCameraCctv, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import moment from 'moment';
import { facialService } from '../../services/central-api/facial';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    eventId?: number;
    facialSent: string;
    facialSentConfidence: string | null | undefined;
}

export default function SmartSearchEventDetailModal({ isModalVisible, onClose, eventId, facialSent, facialSentConfidence }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [event, setEvent] = useState<FacialDetection>();

    async function getDetection() {
        try {
            setIsLoading(true);
            const event = await facialService.getFacialDetection(Number(eventId));

            if (event.person) {
                event.person.facePictures = event.person.facePictures.filter(fp => {
                    const eventCreatedAt = moment(event.createdAt);
                    return moment(fp.createdAt).isBefore(eventCreatedAt) &&
                        (fp.deletedAt === null || moment(fp.deletedAt).isAfter(eventCreatedAt));
                });
            }

            setEvent(event);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused && eventId) {
            getDetection();
        }
    }, [isFocused, eventId]));

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 'auto', maxWidth: '90%', minWidth: 300 }]}>
                    {/* header */}
                    <View style={styles.header}>
                        <MyAppText style={styles.headerTitle}>{translate('general_info')}</MyAppText>
                        <MyAppText style={styles.headerSubTitle}>
                            {translate('availableInfoFor')}{' '}
                            <MyAppText style={styles.headerSubTitleBold}>{translate('reading').toLowerCase()}</MyAppText> {translate('and')}{' '}
                            <MyAppText style={styles.headerSubTitleBold}>{translate('visualization').toLowerCase()}</MyAppText>
                        </MyAppText>
                    </View>
                    {/* body */}
                    <ScrollView style={styles.bodyScrollView} showsVerticalScrollIndicator={false}>
                        {isLoading ? <View style={{ height: 40 }}>
                            <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                        </View> :
                            <View style={[styles.body, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                                <View style={[windowInfo.isMobile ? styles.bodyImageConfidenceMobile : styles.bodyImageConfidence]}>
                                    <View style={styles.bodyImageConfidenceHeader}>
                                        <MyAppText style={styles.bodyImageConfidenceHeaderTitle}>{translate('confidenceLevelsBetweenImages')}</MyAppText>
                                        {!windowInfo.isMobile && <Divider color={theme.colors.primary['200']} />}
                                    </View>
                                    <View style={styles.bodyImageConfidenceContent}>
                                        {facialSent &&
                                            <SmartSearchFacialComparer
                                                capture={event?.imageUrl}
                                                facialSent={facialSent}
                                                confidence={facialSentConfidence}
                                            />}
                                        {event?.person?.facePictures[0].imageUrl &&
                                            <SmartSearchFacialComparer
                                                capture={event?.imageUrl}
                                                reference={event?.person?.facePictures[0].imageUrl}
                                                confidence={event?.confidence}
                                            />}
                                    </View>
                                </View>
                                {!windowInfo.isMobile && <Divider
                                    color={theme.colors.neutral['100']}
                                    orientation='vertical'
                                />}
                                <View style={[styles.bodyCapturePlace, windowInfo.isMobile ? {} : { minWidth: 'auto' }]}>
                                    <View style={styles.bodyCapturePlaceMap}>
                                        <View style={styles.bodyCapturePlaceSectionHeader}>
                                            <MyAppText style={styles.bodyCapturePlaceTitle}>{translate('recordedCaptureLocation')}</MyAppText>
                                            {!windowInfo.isMobile && <Divider color={theme.colors.primary['200']} />}
                                        </View>
                                        <View style={windowInfo.isMobile ? styles.mapContainerMobile : styles.mapContainer}>
                                            <Map
                                                pinnedMarker={{
                                                    lat: event?.camera.latitude ?? 0,
                                                    lng: event?.camera.longitude ?? 0
                                                }}
                                            />
                                        </View>
                                        <View style={styles.bodyCapturePlaceInfo}>
                                            <View style={styles.bodyCapturePlaceInfoRow}>
                                                <FontAwesomeIcon icon={faCameraCctv as IconDefinition} fontSize={16} color={theme.colors.neutral['400']} />
                                                <MyAppText style={styles.bodyCapturePlaceInfoText}>{event?.camera.title}</MyAppText>
                                                <TouchableOpacity onPress={() => window.open(`/cameras/map/${event?.camera.id}`)}>
                                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare as IconDefinition} fontSize={16} color={theme.colors.primary['500']} />
                                                </TouchableOpacity>
                                            </View>
                                            <View style={styles.bodyCapturePlaceInfoRow}>
                                                <FontAwesomeIcon icon={faLocationDot as IconDefinition} fontSize={16} color={theme.colors.neutral['400']} />
                                                <MyAppText style={styles.bodyCapturePlaceInfoText}>{event?.camera.address}</MyAppText>
                                            </View>
                                            <View style={styles.bodyCapturePlaceInfoRow}>
                                                <FontAwesomeIcon icon={faCalendar as IconDefinition} fontSize={16} color={theme.colors.neutral['400']} />
                                                <MyAppText style={styles.bodyCapturePlaceInfoText}>{moment(new Date(event?.time ?? 0)).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
                                            </View>
                                        </View>
                                    </View>
                                    {!windowInfo.isMobile && event?.occurrence && (authenticatedUser?.isAdmin
                                        || authenticatedUser?.permissions.dispatch_module_access
                                        || authenticatedUser?.permissions.dispatch_manage_occurrences
                                        || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences
                                        || authenticatedUser?.permissions.reopen_occurrence
                                        || authenticatedUser?.permissions.transfer_occurrence
                                    ) ?
                                        <View style={styles.bodyCapturePlaceEvent}>
                                            <View style={styles.bodyCapturePlaceSectionHeader}>
                                                <MyAppText style={styles.bodyCapturePlaceTitle}>{translate('eventsRegistry')}</MyAppText>
                                                <Divider color={theme.colors.primary['200']} />
                                            </View>
                                            <View style={styles.bodyCapturePlaceInfoRow}>
                                                <MyAppText style={styles.bodyCapturePlaceInfoText}>{translate('goToOccurrence')}</MyAppText>
                                                <TouchableOpacity onPress={() => window.open(`/dispatch/occurrences/detail/${event.occurrence?.id}`)}>
                                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare as IconDefinition} fontSize={16} color={theme.colors.primary['500']} />
                                                </TouchableOpacity>
                                            </View>
                                        </View> : <></>}
                                </View>
                            </View>}
                    </ScrollView>
                    {/* footer */}
                    <View style={windowInfo.isMobile ? styles.footerMobile : styles.footer}>
                        {windowInfo.isMobile && event?.occurrence && (authenticatedUser?.isAdmin
                            || authenticatedUser?.permissions.dispatch_module_access
                            || authenticatedUser?.permissions.dispatch_manage_occurrences
                            || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences
                            || authenticatedUser?.permissions.reopen_occurrence
                            || authenticatedUser?.permissions.transfer_occurrence
                        ) &&
                            <CustomButton
                                onPress={() => window.open(`/dispatch/occurrences/detail/${event.occurrence?.id}`)}
                                text={translate('goToOccurrence')}
                                type='secondary'
                                style={{ borderWidth: 0 }}
                            />
                        }
                        <CustomButton
                            onPress={() => window.open(`/facial/search/event/${event?.id}`)}
                            text={translate('goToDetection')}
                            type='secondary'
                        />
                        <CustomButton
                            onPress={() => {
                                setEvent(undefined);
                                onClose();
                            }}
                            text={translate('close')}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: {
        maxHeight: '90%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 32,
        rowGap: 24,
        flexWrap: 'wrap'
    },
    header: {
        flexDirection: 'column',
        rowGap: 8,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.modal.header,
    },
    headerSubTitle: {
        fontSize: 16,
        fontWeight: '400',
        color: theme.colors.modal.subheader,
    },
    headerSubTitleBold: {
        fontWeight: '700',
    },
    bodyScrollView: {
        flex: 1,
    },
    body: {
        columnGap: 24,
    },
    bodyImageConfidence: {
        flexDirection: 'column',
        rowGap: 16,
        maxWidth: 400,
        minWidth: 'auto',
        flex: 1.2
    },
    bodyImageConfidenceMobile: {
        flexDirection: 'column',
        rowGap: 16,
        maxWidth: 400,
        zIndex: 2,
        position: 'relative',
        marginBottom: 24,
    },
    bodyImageConfidenceHeader: {
        flexDirection: 'column',
        rowGap: 8,
    },
    bodyImageConfidenceHeaderTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.primary['500'],
    },
    bodyImageConfidenceContent: {
        flexDirection: 'column',
        rowGap: 32,
    },
    bodyCapturePlace: {
        flex: 1,
        flexDirection: 'column',
        rowGap: 32,
        maxWidth: 450,
    },
    bodyCapturePlaceMap: {
        flexDirection: 'column',
        rowGap: 16,
        flex: 1,
    },
    mapContainer: {
        flex: 1,
        width: '100%',
        minHeight: 300,
        maxHeight: 400,
        minWidth: 340,
        borderRadius: 4,
        overflow: 'hidden',
    },
    mapContainerMobile: {
        width: '100%',
        height: 250,
        borderRadius: 4,
        overflow: 'hidden',
        zIndex: 0,
    },
    bodyCapturePlaceInfo: {
        flexDirection: 'column',
        rowGap: 8,
    },
    bodyCapturePlaceInfoRow: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
    },
    bodyCapturePlaceInfoText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
    },
    bodyCapturePlaceEvent: {
        flexDirection: 'column',
        rowGap: 8,
    },
    bodyCapturePlaceSectionHeader: {
        flexDirection: 'column',
        rowGap: 8,
    },
    bodyCapturePlaceTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.primary['500'],
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        columnGap: 8,
    },
    footerMobile: {
        flexDirection: 'column',
        rowGap: 8,
    }
}));
